import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useEffect, useState} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {FetchPolicy} from "@khanacademy/wonder-blocks-data";
import Link from "@khanacademy/wonder-blocks-link";
import {semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import {Body, BodyText, Tagline} from "@khanacademy/wonder-blocks-typography";
import LoginRequired from "@khan/component-screens/login-required";
import {useMutation, useQuery} from "@khan/data-gql";
import {Errors, KAError} from "@khan/logging";
import {PermissionsInfo} from "@khan/shared-core";

/* eslint-disable-next-line @khan/frontend/require-shared-package-imports-only -- TODO: site-banners not migrated yet */
import ResendVerificationEmailButton from "~/site-banners/banners/verify-email/resend-verification-email-button.tsx";

import {SectionLoading} from "../components/section/section-loading.tsx";
import SuccessToast from "../success-toast.tsx";

import EmailSettingsForm from "./email-settings-form.tsx";
import {
    EMAIL_SETTINGS_BY_KAID,
    EMAIL_SETTINGS_BY_TOKEN,
    mapDataToResultWithKaid,
    mapDataToResultWithToken,
} from "./email-settings-query.ts";
import UPDATE_SUBSCRIPTION_BY_TOKEN_MUTATION from "./update-subscription-by-token-mutation.ts";

interface Props {
    token: string | null | undefined;
    targetKaid: string | null | undefined;
}

export const EmailSettings: React.FC<Props> = ({token, targetKaid}) => {
    const [showSuccessToast, setShowSuccessToast] = useState(false);
    const [updateSubscription] = useMutation(
        UPDATE_SUBSCRIPTION_BY_TOKEN_MUTATION,
    );

    // Determine which query should be active
    const shouldUseTokenQuery = Boolean(token && !targetKaid);

    // Both hooks are always called, but only one will actually execute
    const [tokenResult] = useQuery(EMAIL_SETTINGS_BY_TOKEN, {
        variables: {token: token || ""},
        fetchPolicy: FetchPolicy.CacheAndNetwork,
        skip: !shouldUseTokenQuery,
    });

    const [kaidResult] = useQuery(EMAIL_SETTINGS_BY_KAID, {
        variables: {targetKaid: targetKaid || ""},
        fetchPolicy: FetchPolicy.CacheAndNetwork,
        skip: shouldUseTokenQuery,
    });

    // Select the result from whichever query was not skipped
    const queryResult = shouldUseTokenQuery ? tokenResult : kaidResult;

    // Map the result data to the new format like before
    const mappedResult = () => {
        if (tokenResult.status === "success" && tokenResult.data) {
            return mapDataToResultWithToken({
                data: tokenResult.data,
                loading: false,
                isError: false,
            });
        }

        if (kaidResult.status === "success" && kaidResult.data) {
            return mapDataToResultWithKaid({
                data: kaidResult.data,
                loading: false,
                isError: false,
            });
        }

        if (queryResult.status === "loading") {
            return {
                isLoading: true,
                isError: false,
                target: null,
            };
        }

        if (queryResult.status === "error") {
            return {
                isLoading: false,
                isError: true,
                target: null,
            };
        }

        return {
            isLoading: false,
            isError: false,
            target: null,
        };
    };

    useEffect(() => {
        if (token) {
            const performUnsubscribe = async () => {
                try {
                    const mutationResult = await updateSubscription({
                        token,
                        subscribe: false,
                    });

                    if (mutationResult.status === "error") {
                        throw new KAError(
                            "Failed to update subscription",
                            Errors.Internal,
                            {cause: mutationResult.error},
                        );
                    }

                    if (
                        mutationResult.status === "success" &&
                        mutationResult.data?.updateSubscriptionByToken?.error
                    ) {
                        throw new KAError(
                            mutationResult.data.updateSubscriptionByToken.error.code,
                            Errors.Internal,
                        );
                    }

                    setShowSuccessToast(true);
                } catch (error) {
                    // Handle error silently like in original implementation
                }
            };

            performUnsubscribe();
        }
    }, [token, updateSubscription]);

    const {target, isLoading, isError} = mappedResult();

    if (isLoading) {
        return <SectionLoading numberOfSections={1} />;
    }

    if (!target || isError) {
        // We allow a user to query for email settings while
        // not logged in only if they possess a token. If the
        // token is invalid, we redirect them to log in.
        if (token && !PermissionsInfo.isLoggedIn) {
            return <LoginRequired />;
        }
        return (
            <Tagline style={styles.text}>
                {t`Oops! Looks like we don't have data available for this user.`}
            </Tagline>
        );
    }

    if (target.isChild) {
        // Child accounts are prevented from navigating here, but just in
        // case someone follows a link:
        return (
            <View style={styles.infoContainer}>
                <Body style={styles.infoText}>
                    {t`Sorry, email notifications are not available for child accounts. Come back after you turn 13!`}
                </Body>
                <Link href="/settings/account">
                    {t`Back to account settings`}
                </Link>
            </View>
        );
    }

    if (!target.hasSendableEmail) {
        return (
            <View style={styles.infoContainer}>
                <BodyText style={styles.infoText}>
                    {t`If you connect an email to your account, we'll be able to send you notifications and announcements about new stuff on Khan Academy. Don't worry though, we hate spam too, so we make it really easy to unsubscribe.`}
                </BodyText>
                <Link href="/settings/account#linked-accounts">
                    {t`Connect an email`}
                </Link>
            </View>
        );
    }

    if (!target.isEmailVerified && target.from === "kaid") {
        const {pendingEmailVerifications, signupDataIfUnverified} = target;
        const {email} = pendingEmailVerifications?.[0] ?? {};
        const unverifiedEmail =
            email || (signupDataIfUnverified && signupDataIfUnverified.email);
        return (
            <View style={styles.leftAlign}>
                <BodyText style={styles.infoText}>
                    {t`Once you open the email we sent and finish signing up, you'll be able to receive other emails from Khan Academy.`}
                </BodyText>
                <ResendVerificationEmailButton email={unverifiedEmail} />
            </View>
        );
    }

    return (
        <>
            <EmailSettingsForm
                token={token}
                targetKaid={targetKaid}
                target={target}
            />

            {showSuccessToast && (
                <SuccessToast onClose={() => setShowSuccessToast(false)} />
            )}
        </>
    );
};

const styles = StyleSheet.create({
    text: {
        color: semanticColor.core.foreground.neutral.strong,
    },
    leftAlign: {
        alignItems: "flex-start",
    },
    infoContainer: {
        display: "flex",
        flexDirection: "column",
        gap: sizing.size_120,
    },
    infoText: {
        marginBlockEnd: sizing.size_120,
    },
});

export default EmailSettings;
